import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import useHttp from '../../hooks/http.hook'
import FullscreenSpinner from '../components/FullscreenSpinner'
import InsufficientStockModal from './components/InsufficientStockModal'
import PreOrderStockModal from './components/PreOrderStockModal'
import './BranchesPage.css'

function ensureLeafletLoaded() {
  // Load Leaflet via CDN (no npm deps). Safe to call multiple times.
  if (window.L) return Promise.resolve(window.L)

  const existingScript = document.querySelector('script[data-leaflet="true"]')
  if (existingScript) {
    if (window.L) return Promise.resolve(window.L)
    return new Promise((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(window.L))
      existingScript.addEventListener('error', reject)
    })
  }

  if (!document.querySelector('link[data-leaflet="true"]')) {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
    link.setAttribute('data-leaflet', 'true')
    link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY='
    link.crossOrigin = ''
    document.head.appendChild(link)
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
    script.defer = true
    script.setAttribute('data-leaflet', 'true')
    script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo='
    script.crossOrigin = ''
    script.onload = () => resolve(window.L)
    script.onerror = reject
    document.body.appendChild(script)
  })
}

function isRentalCart(items) {
  return (items || []).some((item) => item?.fromAt && item?.toAt)
}

function lineStock(branch, item) {
  const counts = branch?.productCounts || []
  const match = counts.find((count) =>
    String(count?.productId) === String(item?.id)
    && count?.fromAt === item?.fromAt
    && count?.toAt === item?.toAt
  )
  return match ? Number(match.count || 0) : 0
}

function getBranchStockMaps(branch) {
  const available = new Map()
  const preOrderById = new Map()
  const counts = branch?.productCounts || []
  for (const c of counts) {
    const pid = c?.productId
    if (!pid) continue
    available.set(String(pid), Number(c?.count || 0))
    preOrderById.set(String(pid), !!c?.preOrder)
  }
  return { available, preOrderById }
}

function getCartSourceItems(cartItems, requiredByProductId) {
  if (Array.isArray(cartItems) && cartItems.length) return cartItems
  return [...requiredByProductId.entries()].map(([id, qty]) => ({
    id,
    key: id,
    name: `Товар #${id}`,
    quantity: qty,
  }))
}

function getInsufficientItems(branch, cartItems, requiredByProductId) {
  if (isRentalCart(cartItems)) {
    const short = cartItems
      .map((item) => {
        const needed = Number(item?.quantity || 0)
        const have = lineStock(branch, item)
        if (!(needed > 0) || have >= needed) return null
        return { ...item, needed, available: have }
      })
      .filter(Boolean)
    if (short.length > 0 || branch?.rentalFits !== false) return short
    return cartItems.map((item) => ({
      ...item,
      needed: Number(item?.quantity || 0),
      available: lineStock(branch, item),
    }))
  }
  const { available, preOrderById } = getBranchStockMaps(branch)
  return getCartSourceItems(cartItems, requiredByProductId)
    .map((it) => {
      const pid = String(it?.id ?? '')
      if (!pid) return null
      const needed = requiredByProductId.get(pid) ?? Number(it?.quantity || 0)
      const have = available.get(pid) ?? 0
      if (!(needed > 0) || have >= needed || preOrderById.get(pid)) return null
      return {
        ...it,
        needed,
        available: have,
      }
    })
    .filter(Boolean)
}

function parseLatLng(location) {
  const lat = Number(location?.lat)
  const lng = Number(location?.lng)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return [lat, lng]
}

const DAY_NAMES = {
  1: 'Пн',
  2: 'Вт',
  3: 'Ср',
  4: 'Чт',
  5: 'Пт',
  6: 'Сб',
  7: 'Вс',
}

function formatSchedule(schedule) {
  if (!Array.isArray(schedule) || !schedule.length) return null
  const groups = new Map()
  for (const entry of schedule) {
    const day = Number(entry?.day)
    if (!day) continue
    const open = entry?.open ?? ''
    const close = entry?.close ?? ''
    const key = `${open}–${close}`
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(day)
  }
  const parts = [...groups.entries()].map(([hours, days]) => {
    days.sort((a, b) => a - b)
    const segments = []
    let start = days[0]
    let prev = days[0]
    for (let i = 1; i <= days.length; i += 1) {
      if (days[i] === prev + 1) {
        prev = days[i]
        continue
      }
      segments.push(start === prev ? DAY_NAMES[start] : `${DAY_NAMES[start]}–${DAY_NAMES[prev]}`)
      start = days[i]
      prev = days[i]
    }
    return `${segments.join(', ')} ${hours}`
  })
  return parts.join(' · ')
}

function getPreOrderItems(branch, cartItems, requiredByProductId) {
  if (isRentalCart(cartItems)) return []
  const { available, preOrderById } = getBranchStockMaps(branch)
  return getCartSourceItems(cartItems, requiredByProductId)
    .map((it) => {
      const pid = String(it?.id ?? '')
      if (!pid) return null
      const needed = requiredByProductId.get(pid) ?? Number(it?.quantity || 0)
      const have = available.get(pid) ?? 0
      if (!(needed > 0) || have >= needed || !preOrderById.get(pid)) return null
      return {
        ...it,
        needed,
        available: have,
      }
    })
    .filter(Boolean)
}

function BranchesPage() {
  const { business } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { requestWithMeta } = useHttp()

  const mapElRef = useRef(null)
  const leafletMapRef = useRef(null)
  const leafletMarkersRef = useRef(null)
  const invalidateSizeTimeoutRef = useRef(null)
  const didAutoNavigateRef = useRef(false)
  const didAutoShowStockModalRef = useRef(false)

  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [mapReady, setMapReady] = useState(false)
  const [view, setView] = useState('list')
  const [error, setError] = useState(null)
  const [insufficientBranch, setInsufficientBranch] = useState(null)
  const [preOrderBranch, setPreOrderBranch] = useState(null)

  const requiredByProductId = useMemo(() => {
    const map = new Map()
    const fromState = location.state?.cartItems
    if (Array.isArray(fromState) && fromState.length) {
      for (const it of fromState) {
        const id = it?.id
        const qty = Number(it?.quantity || 0)
        if (!id || !Number.isFinite(qty) || qty <= 0) continue
        map.set(String(id), qty)
      }
      return map
    }

    // If we only have query params, treat each as quantity 1.
    const qs = new URLSearchParams(location.search)
    const ids = qs.getAll('productIds').filter(Boolean)
    ids.forEach((id) => map.set(String(id), 1))
    return map
  }, [location.state, location.search])

  const productIds = useMemo(() => {
    const qs = new URLSearchParams(location.search)
    const fromQuery = qs.getAll('productIds').filter(Boolean)
    if (fromQuery.length) return fromQuery

    const fromState = (location.state?.cartItems || [])
      .map((it) => it?.id)
      .filter(Boolean)

    return fromState
  }, [location.search, location.state])

  const cartItems = useMemo(() => {
    const items = location.state?.cartItems
    return Array.isArray(items) ? items : []
  }, [location.state])

  const branchHasEnough = useCallback((branch) => {
    if (isRentalCart(cartItems)) return branch?.rentalFits !== false
    if (!requiredByProductId || requiredByProductId.size === 0) return true
    const { available, preOrderById } = getBranchStockMaps(branch)
    for (const [pid, needed] of requiredByProductId.entries()) {
      const have = available.get(String(pid)) ?? 0
      if (have < needed && !preOrderById.get(String(pid))) return false
    }
    return true
  }, [cartItems, requiredByProductId])

  const insufficientItems = useMemo(() => {
    if (!insufficientBranch) return []
    return getInsufficientItems(insufficientBranch, cartItems, requiredByProductId)
  }, [insufficientBranch, cartItems, requiredByProductId])

  const preOrderItems = useMemo(() => {
    if (!preOrderBranch) return []
    return getPreOrderItems(preOrderBranch, cartItems, requiredByProductId)
  }, [preOrderBranch, cartItems, requiredByProductId])

  const goToBooking = useCallback((branch) => {
    const bid = branch?._id ?? branch?.id
    if (!bid || !business) return
    navigate(`/${encodeURIComponent(business)}/branches/${encodeURIComponent(bid)}/booking`, {
      state: { cartItems },
    })
  }, [cartItems, navigate, business])

  const handleBranchSelect = useCallback((branch) => {
    if (!branch) return
    const hardItems = getInsufficientItems(branch, cartItems, requiredByProductId)
    if (hardItems.length > 0) {
      setPreOrderBranch(null)
      setInsufficientBranch(branch)
      return
    }
    const softItems = getPreOrderItems(branch, cartItems, requiredByProductId)
    if (softItems.length > 0) {
      setInsufficientBranch(null)
      setPreOrderBranch(branch)
      return
    }
    goToBooking(branch)
  }, [cartItems, goToBooking, requiredByProductId])

  useEffect(() => {
    const fetchBranches = async () => {
      if (!business) {
        setError('Business is required')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        const qs = new URLSearchParams()
        productIds.forEach((id) => qs.append('productIds', id))
        if (isRentalCart(cartItems)) {
          qs.set('lines', JSON.stringify(cartItems.map((item) => ({
            productId: item.id,
            quantity: item.quantity,
            fromAt: item.fromAt,
            toAt: item.toAt,
          }))))
        }

        const { ok, data, status } = await requestWithMeta(
          `/business/${encodeURIComponent(business)}/branches${qs.toString() ? `?${qs.toString()}` : ''}`
        )

        if (!ok) {
          if (status === 404) setError('Business not found')
          else setError(data?.error || data?.message || 'Ошибка загрузки филиалов')
          return
        }

        setBranches(data?.branches || [])
      } catch (e) {
        setError(e.message || 'Ошибка загрузки филиалов')
      } finally {
        setLoading(false)
      }
    }

    fetchBranches()
  }, [business, productIds, cartItems, requestWithMeta])

  useEffect(() => {
    if (didAutoNavigateRef.current || didAutoShowStockModalRef.current) return
    if (loading || error) return
    if (!Array.isArray(branches) || branches.length !== 1) return

    const b = branches[0]
    const bid = b?._id ?? b?.id
    if (!bid) return

    const hardItems = getInsufficientItems(b, cartItems, requiredByProductId)
    if (hardItems.length > 0) {
      didAutoShowStockModalRef.current = true
      setInsufficientBranch(b)
      return
    }

    const softItems = getPreOrderItems(b, cartItems, requiredByProductId)
    if (softItems.length > 0) {
      didAutoShowStockModalRef.current = true
      setPreOrderBranch(b)
      return
    }

    didAutoNavigateRef.current = true
    goToBooking(b)
  }, [
    branches,
    cartItems,
    error,
    goToBooking,
    loading,
    requiredByProductId,
  ])

  useEffect(() => {
    if (view !== 'map') return undefined
    let cancelled = false

    async function initMap() {
      if (!mapElRef.current) return
      const L = await ensureLeafletLoaded()
      if (cancelled) return
      if (leafletMapRef.current) return

      const fallbackLat = 42.87658553054612
      const fallbackLng = 74.6036089976348
      const firstPoint = parseLatLng(branches?.[0]?.location)
      const initialLat = firstPoint ? firstPoint[0] : fallbackLat
      const initialLng = firstPoint ? firstPoint[1] : fallbackLng

      const map = L.map(mapElRef.current, {
        zoomControl: false,
        attributionControl: false,
        // Helps avoid "zoom transition end after unmount" crashes
        zoomAnimation: false,
        fadeAnimation: false,
        markerZoomAnimation: false,
        // Less sensitive scroll-wheel zoom on laptop/trackpad (more px per zoom step)
        wheelPxPerZoomLevel: 80,
        wheelDebounceTime: 100,
        // Finer zoom steps so each scroll feels less jumpy
        zoomSnap: 0.5,
        zoomDelta: 0.5,
        // Limit zoom range so one step doesn't take you too far in/out
        minZoom: 10,
        maxZoom: 17,
      }).setView([initialLat, initialLng], 12)

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(map)

      leafletMarkersRef.current = L.layerGroup().addTo(map)
      leafletMapRef.current = map
      if (cancelled) {
        map.remove()
        leafletMapRef.current = null
        leafletMarkersRef.current = null
        return
      }
      setMapReady(true)
      invalidateSizeTimeoutRef.current = setTimeout(() => {
        if (cancelled) return
        if (!leafletMapRef.current) return
        map.invalidateSize()
      }, 0)
    }

    initMap()

    return () => {
      cancelled = true
      if (invalidateSizeTimeoutRef.current) {
        clearTimeout(invalidateSizeTimeoutRef.current)
        invalidateSizeTimeoutRef.current = null
      }
      if (leafletMapRef.current) {
        // Stop any in-flight pan/zoom animations & detach handlers before removal
        leafletMapRef.current.stop?.()
        leafletMapRef.current.off?.()
        leafletMapRef.current.remove()
        leafletMapRef.current = null
      }
      leafletMarkersRef.current = null
      setMapReady(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  useEffect(() => {
    const map = leafletMapRef.current
    const L = window.L
    const layer = leafletMarkersRef.current
    if (!mapReady || !map || !L || !layer) return
    // If we’re about to redirect (single branch auto-select), avoid triggering a zoom
    // that could finish after unmount and crash Leaflet.
    if (didAutoNavigateRef.current) return

    layer.clearLayers()

    const points = []
    for (const b of branches) {
      const point = parseLatLng(b?.location)
      if (!point) continue

      points.push(point)
      const hasEnough = branchHasEnough(b)
      const needsPreOrder = hasEnough && getPreOrderItems(b, cartItems, requiredByProductId).length > 0
      const logoUrl = b?.logoUrl || b?.businessLogoUrl || ''
      const safeLogoUrl = String(logoUrl || '').replace(/"/g, '&quot;')

      const icon = L.divIcon({
        className: 'client-branch-pinIcon',
        html: `
          <div class="client-branch-pin ${hasEnough ? '' : 'client-branch-pin--insufficient'}" role="img" aria-label="Branch">
            <div class="client-branch-pinBadge">
              ${
                safeLogoUrl
                  ? `<img class="client-branch-pinLogo" src="${safeLogoUrl}" alt="" />`
                  : `<div class="client-branch-pinLogo client-branch-pinLogo--placeholder"></div>`
              }
            </div>
            <div class="client-branch-pinArrow" aria-hidden="true"></div>
          </div>
        `,
        iconSize: [56, 72],
        iconAnchor: [28, 72],
        popupAnchor: [0, -68],
      })

      const popup = !hasEnough
        ? `Недостаточно товаров на складе`
        : needsPreOrder
          ? `Недостаточно на складе — доступен предзаказ`
          : `Филиал: ${b?.name || b?._id || ''}`
      const marker = L.marker(point, { icon }).bindPopup(popup).addTo(layer)
      marker.on('click', () => {
        handleBranchSelect(b)
      })
    }

    const applyView = () => {
      if (points.length >= 2) map.fitBounds(points, { padding: [40, 40] })
      else if (points.length === 1) map.setView(points[0], 15)
    }

    applyView()

    // Spinner overlay / late layout: re-measure after the map is visible so pins
    // aren't left off-screen from a zero-size fitBounds during load.
    if (loading) return undefined
    const t = setTimeout(() => {
      if (!leafletMapRef.current) return
      map.invalidateSize()
      applyView()
    }, 0)
    return () => clearTimeout(t)
  }, [mapReady, loading, branches, branchHasEnough, cartItems, handleBranchSelect, requiredByProductId])

  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) return
    const map = leafletMapRef.current
    const L = window.L
    if (!map || !L) return

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Number(pos.coords.latitude.toFixed(6))
        const lng = Number(pos.coords.longitude.toFixed(6))
        map.setView([lat, lng], 15)
        L.circleMarker([lat, lng], {
          radius: 8,
          color: '#3b82f6',
          weight: 2,
          fillColor: '#93c5fd',
          fillOpacity: 0.5,
        }).addTo(map)
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    )
  }, [])

  return (
    <div className="client-branches-page">
      <FullscreenSpinner open={loading} label="Загрузка филиалов…" />

      <div className="client-branches-header">
        <div className="client-branches-toggle" role="tablist" aria-label="Вид списка филиалов">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'list'}
            className={`client-branches-toggleBtn${view === 'list' ? ' client-branches-toggleBtn--active' : ''}`}
            onClick={() => setView('list')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M8 6h13M8 12h13M8 18h13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <circle cx="4" cy="6" r="1.6" fill="currentColor" />
              <circle cx="4" cy="12" r="1.6" fill="currentColor" />
              <circle cx="4" cy="18" r="1.6" fill="currentColor" />
            </svg>
            <span>Список</span>
          </button>
          <span className="client-branches-toggleSep" aria-hidden="true">|</span>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'map'}
            className={`client-branches-toggleBtn${view === 'map' ? ' client-branches-toggleBtn--active' : ''}`}
            onClick={() => setView('map')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 4 3.5 6v14L9 18l6 2 5.5-2V4L15 6 9 4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              <path d="M9 4v14M15 6v14" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
            <span>Карта</span>
          </button>
        </div>
      </div>

      {!loading && error && (
        <div className="client-branches-error">
          <div className="client-branches-chip client-branches-chipError">{error}</div>
        </div>
      )}

      {view === 'list' && !loading && !error && (
        <h1 className="client-branches-title">Выберите филиал</h1>
      )}

      {view === 'map' ? (
        <div className="client-branches-mapWrap">
          <div ref={mapElRef} className="client-branches-map" />

          <div className="client-branches-controls" aria-hidden="false">
            <button
              className="client-branches-fab client-branches-fabMyLoc"
              type="button"
              onClick={useMyLocation}
              aria-label="My location"
              title="My location"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 2v3m0 14v3M2 12h3m14 0h3"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="12" r="6.5" stroke="currentColor" strokeWidth="2.2" />
                <circle cx="12" cy="12" r="1.2" fill="currentColor" />
              </svg>
            </button>
          </div>

          {!loading && !error && (
            <div className="client-branches-status">
              <div className="client-branches-chip">
                Выберите филиал
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="client-branches-list">
          {branches.map((branch) => {
            const bid = branch?._id ?? branch?.id
            const hasEnough = branchHasEnough(branch)
            const needsPreOrder = hasEnough && getPreOrderItems(branch, cartItems, requiredByProductId).length > 0
            const schedule = formatSchedule(branch?.schedule)
            return (
              <button
                key={bid}
                type="button"
                className={`client-branch-card${hasEnough ? '' : ' client-branch-card--insufficient'}`}
                onClick={() => handleBranchSelect(branch)}
              >
                <span className="client-branch-cardInfo">
                  <span className="client-branch-cardName">{branch?.name || `Филиал #${bid}`}</span>
                  {schedule ? <span className="client-branch-cardSchedule">{schedule}</span> : null}
                  {!hasEnough ? <span className="client-branch-cardTag">Недостаточно товаров</span> : null}
                </span>
                {needsPreOrder ? (
                  <span className="client-branch-cardSide">предзаказ</span>
                ) : (
                  <span className="client-branch-cardArrow" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                )}
              </button>
            )
          })}
          {!loading && !error && branches.length === 0 && (
            <div className="client-branches-empty">Филиалы не найдены</div>
          )}
        </div>
      )}

      <InsufficientStockModal
        isOpen={!!insufficientBranch}
        items={insufficientItems}
        onClose={() => setInsufficientBranch(null)}
      />

      <PreOrderStockModal
        isOpen={!!preOrderBranch}
        items={preOrderItems}
        onClose={() => setPreOrderBranch(null)}
        onContinue={() => {
          const branch = preOrderBranch
          setPreOrderBranch(null)
          goToBooking(branch)
        }}
      />
    </div>
  )
}

export default BranchesPage
